'use client';

import { useState } from 'react';

interface Props {
  startPage?: number;
  category?: string | null;
}

type RichTextNode = { text?: string } | string;
type Category = {
  uid?: string;
  id?: string;
  data?: { name?: string };
  name?: string;
} | null;
type BlogData = {
  card_title?: RichTextNode[] | string;
  title?: RichTextNode[] | string;
  image?: { url?: string } | null;
  category?: Category;
  all_categories?: Array<{ category?: Category }>;
  [k: string]: unknown;
};

export default function PopularLoadMoreServer({
  startPage = 1,
  category,
}: Props) {
  const [page, setPage] = useState(startPage);
  const [loading, setLoading] = useState(false);

  const loadMore = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', '3');
      if (category) params.append('category', category);

      const res = await fetch(`/api/blog/popular/html?${params.toString()}`);
      const json = await res.json();
      if (json?.results && json.results.length > 0) {
        const grid = document.getElementById('grid-container');
        const newsletter = grid?.querySelector(
          '[data-newsletter]',
        ) as HTMLElement | null;
        // The newsletter lives inside the inner grid (grid > div.grid ... > [data-newsletter])
        const innerGrid = newsletter?.parentElement;
        if (newsletter && innerGrid) {
          json.results.forEach(
            (it: {
              uid?: string;
              date?: string | null;
              image?: string | null;
              data?: unknown;
              categoryUid?: string | null;
              categoryName?: string | null;
            }) => {
              const wrapper = document.createElement('div');
              // Use classes that mirror BlogCard 'new-simple' to match visuals
              wrapper.className =
                'flex flex-col rounded-[20px] overflow-hidden text-start text-white h-full';
              // inline background to match BlogCard new-simple style
              wrapper.setAttribute(
                'style',
                'background: linear-gradient(to right, rgba(255,255,255,0.15), rgba(3,191,158,0.15), rgba(255,255,255,0.15))',
              );

              // Use raw data shape to compute title, image and category like server does
              const rawData = (it.data ?? {}) as { blog?: BlogData } & BlogData;
              const blogData = (rawData.blog ?? rawData) as BlogData;

              // Title may be rich text
              let titleText = '';
              const titleField = blogData.card_title ?? blogData.title ?? '';
              if (Array.isArray(titleField)) {
                titleText = titleField
                  .map((n: RichTextNode) =>
                    typeof n === 'string'
                      ? n
                      : n && typeof n === 'object'
                        ? (n.text ?? '')
                        : '',
                  )
                  .filter(Boolean)
                  .join(' ');
              } else {
                titleText = String(titleField || '');
              }
              const safeTitle = titleText
                .replace(/</g, '&lt;')
                .replace(/"/g, '&quot;');

              // Image
              let safeImage: string | null = null;
              if (
                blogData &&
                typeof blogData === 'object' &&
                'image' in blogData
              ) {
                const img = blogData.image;
                if (
                  img &&
                  typeof img === 'object' &&
                  typeof img.url === 'string'
                ) {
                  safeImage = String(img.url);
                }
              }
              if (!safeImage && it.image) safeImage = String(it.image);

              // Category: prefer API-provided fields, then fall back to blogData shapes
              const apiCategoryUid = it.categoryUid ?? null;
              const apiCategoryName = it.categoryName ?? null;

              let firstCategory: Category = null;
              if (blogData?.category) {
                firstCategory = blogData.category;
              } else if (
                blogData?.all_categories &&
                Array.isArray(blogData.all_categories)
              ) {
                firstCategory = blogData.all_categories[0]?.category ?? null;
              } else if (rawData?.category) {
                firstCategory = rawData.category;
              }

              const computedCategoryUid =
                firstCategory?.uid ?? firstCategory?.id ?? null;
              const computedCategoryName =
                firstCategory?.data?.name ?? firstCategory?.name ?? '';

              const finalCategoryUid = apiCategoryUid ?? computedCategoryUid;
              const finalCategoryName =
                (apiCategoryName ?? computedCategoryName) || '';
              const safeCategoryName = (finalCategoryName || '').toUpperCase();
              const categoryHtml = safeCategoryName
                ? finalCategoryUid
                  ? `<a href="/blog?category=${finalCategoryUid}" class="text-xs text-[#05F273] font-semibold tracking-wider no-underline">${safeCategoryName}</a>`
                  : `<span class="text-xs text-[#05F273] font-semibold tracking-wider">${safeCategoryName}</span>`
                : '';

              wrapper.innerHTML = `
              ${
                safeImage
                  ? `
                <div class="relative h-48">
                  <a href="/blog/${it.uid}">
                    <img src="${safeImage}" alt="${safeTitle}" class="h-full w-full object-cover rounded-t-[20px]" />
                  </a>
                  <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div class="absolute top-4 right-4 text-white"></div>
                </div>
              `
                  : ''
              }
              <div class="p-6 flex flex-col justify-between h-full">
                <div>
                  <a href="/blog/${it.uid}"><h3 class="text-xl font-bold text-white mb-2">${safeTitle}</h3></a>
                </div>
                <div class="flex flex-col gap-4">
                  <p class="text-green-accent text-sm font-semibold uppercase">
                    ${categoryHtml}
                  </p>
                  <div class="flex items-center space-x-3">
                    ${it.date ? `<div class="text-sm">${new Date(it.date).toLocaleDateString()}</div>` : ''}
                  </div>
                </div>
              </div>
            `;
              innerGrid.insertBefore(wrapper, newsletter);
            },
          );
        }
        setPage((p) => p + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center w-full mt-8">
      <button
        className={`secondary-button ${loading ? 'opacity-60 pointer-events-none' : ''}`}
        onClick={loadMore}
        disabled={loading}
        aria-label="Load more popular posts"
      >
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}
